/**
 * PolicySlice
 * 
 * Redux slice for managing policy state.
 * 
 * This slice follows the modular pattern with:
 * - state.ts: State interface and initial state
 * - selectors.ts: Memoized selectors
 * - extraReducers.ts: API endpoint matchers
 * - reducers/: Split reducer files by domain
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, PolicySliceState } from './state';
import { buildPolicyExtraReducers } from './extraReducers';

// Import all reducers (renamed to avoid conflicts with exported actions)
import {
  initializePolicy as initializePolicyReducer,
  updatePolicy as updatePolicyReducer,
  clearPolicy as clearPolicyReducer,
  setSelectedCompany as setSelectedCompanyReducer,
  clearPolicies as clearPoliciesReducer,
  addProcedureCode as addProcedureCodeReducer,
  removeProcedureCode as removeProcedureCodeReducer,
  setProcedureCodes as setProcedureCodesReducer,
  addInsurance as addInsuranceReducer,
  removeInsurance as removeInsuranceReducer,
  setInsurances as setInsurancesReducer,
  markFieldAsEdited as markFieldAsEditedReducer,
  clearEditedFields as clearEditedFieldsReducer,
  markAsSaved as markAsSavedReducer,
  discardChanges as discardChangesReducer,
  setValidationError as setValidationErrorReducer,
  clearValidationError as clearValidationErrorReducer,
  clearAllValidationErrors as clearAllValidationErrorsReducer,
} from './reducers';

/**
 * Policy slice definition
 */
const policySlice = createSlice({
  name: 'policy',
  initialState,
  reducers: {
    // Core reducers
    initializePolicy: initializePolicyReducer,
    updatePolicy: updatePolicyReducer,
    clearPolicy: clearPolicyReducer,
    setSelectedCompany: setSelectedCompanyReducer,
    clearPolicies: clearPoliciesReducer,

    // Procedure codes reducers
    addProcedureCode: addProcedureCodeReducer,
    removeProcedureCode: removeProcedureCodeReducer,
    setProcedureCodes: setProcedureCodesReducer,

    // Insurances reducers
    addInsurance: addInsuranceReducer,
    removeInsurance: removeInsuranceReducer,
    setInsurances: setInsurancesReducer,

    // Edit tracking reducers
    markFieldAsEdited: markFieldAsEditedReducer,
    clearEditedFields: clearEditedFieldsReducer,
    markAsSaved: markAsSavedReducer,
    discardChanges: discardChangesReducer,

    // Validation reducers
    setValidationError: setValidationErrorReducer,
    clearValidationError: clearValidationErrorReducer,
    clearAllValidationErrors: clearAllValidationErrorsReducer,
  },
  extraReducers: buildPolicyExtraReducers,
});

// Export actions
export const policyActions = policySlice.actions;

// Export individual actions with Action suffix for convenience
export const {
  initializePolicy: initializePolicyAction,
  updatePolicy: updatePolicyAction,
  clearPolicy: clearPolicyAction,
  setSelectedCompany: setSelectedCompanyAction,
  clearPolicies: clearPoliciesAction,
  addProcedureCode: addProcedureCodeAction,
  removeProcedureCode: removeProcedureCodeAction,
  setProcedureCodes: setProcedureCodesAction,
  addInsurance: addInsuranceAction,
  removeInsurance: removeInsuranceAction,
  setInsurances: setInsurancesAction,
  markFieldAsEdited: markFieldAsEditedAction,
  clearEditedFields: clearEditedFieldsAction,
  markAsSaved: markAsSavedAction,
  discardChanges: discardChangesAction,
  setValidationError: setValidationErrorAction,
  clearValidationError: clearValidationErrorAction,
  clearAllValidationErrors: clearAllValidationErrorsAction,
} = policySlice.actions;

// Also export with original names for backward compatibility
export const {
  initializePolicy,
  updatePolicy,
  clearPolicy,
  setSelectedCompany,
  clearPolicies,
  addProcedureCode,
  removeProcedureCode,
  setProcedureCodes,
  addInsurance,
  removeInsurance,
  setInsurances,
  markFieldAsEdited,
  clearEditedFields,
  markAsSaved,
  discardChanges,
  setValidationError,
  clearValidationError,
  clearAllValidationErrors,
} = policySlice.actions;

// Export reducer
export default policySlice.reducer;

// Re-export state types
export type { PolicySliceState } from './state';

// Re-export selectors
export * from './selectors';
